import {
  formatearDia,
  formatearFecha,
  formatearFechaHora,
  formatearRevision,
} from "@gestiondoc/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTRPC } from "../api.ts";
import { Encabezado, Kpi } from "../componentes/Encabezado.tsx";
import {
  Aviso,
  Boton,
  CABECERA,
  CLASE_ENTRADA,
  Enlace,
  EstadoVacio,
  Pestanas,
  Tabla,
  TD,
  TH,
} from "../componentes/ui.tsx";
import { abreviarNombre, mensajeDe } from "../utiles.ts";

const PESTANAS = [
  { clave: "documentos", nombre: "Control de documentos" },
  { clave: "registros", nombre: "Control de registros" },
  { clave: "actividad", nombre: "Registro de actividad" },
  { clave: "acuses", nombre: "Acuses de lectura" },
] as const;
type Pestana = (typeof PESTANAS)[number]["clave"];

type Filtro = { procesoCodigo: string | null; desde: Date | null; hasta: Date | null };

/** "2026-01-01" del input de fecha → ese día en Honduras (UTC−6, sin horario de verano). */
const aFecha = (valor: string, fin = false): Date | null =>
  valor ? new Date(`${valor}T${fin ? "23:59:59.999" : "00:00:00.000"}-06:00`) : null;

/**
 * Pantalla 06 · Reportes de auditoría. Cuatro pestañas con el mismo filtro (proceso y periodo),
 * los KPIs arriba y la exportación a Excel con exactamente lo que se ve.
 */
export function PaginaAuditoria() {
  const trpc = useTRPC();
  const [pestana, setPestana] = useState<Pestana>("documentos");
  const [proceso, setProceso] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const filtro: Filtro = {
    procesoCodigo: proceso || null,
    desde: aFecha(desde),
    hasta: aFecha(hasta, true),
  };

  const procesos = useQuery(trpc.auditoria.procesos.queryOptions());
  const resumen = useQuery({
    ...trpc.auditoria.resumen.queryOptions(filtro),
    placeholderData: keepPreviousData,
  });

  const consulta = new URLSearchParams();
  if (proceso) consulta.set("proceso", proceso);
  if (desde) consulta.set("desde", desde);
  if (hasta) consulta.set("hasta", hasta);
  const cola = consulta.size ? `?${consulta}` : "";

  const r = resumen.data;
  return (
    <div>
      <Encabezado
        overline="Sistema"
        titulo="Reportes de auditoría"
        acciones={
          <>
            <Enlace href={`/reportes/auditoria.xlsx${cola}`}>Exportar Excel</Enlace>
            <Enlace href={`/reportes/auditoria.pdf${cola}`}>Exportar PDF</Enlace>
            <Enlace
              variante="primario"
              href={`/reportes/paquete.zip${cola}`}
              title="Un zip con los PDF vigentes, los reportes en Excel y PDF, y la verificación de la cadena"
            >
              Generar paquete de auditoría
            </Enlace>
          </>
        }
      />

      {resumen.error && <Aviso className="mb-4">{mensajeDe(resumen.error)}</Aviso>}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi valor={r?.vigentes ?? "—"} etiqueta="Documentos vigentes en el sistema" />
        <Kpi
          valor={r ? `${r.conTresFirmas} %` : "—"}
          etiqueta="Con las tres firmas registradas"
          tono="verde"
        />
        <Kpi valor={r?.revisionVencida ?? "—"} etiqueta="Con revisión anual vencida" tono="ambar" />
        <Kpi
          valor={r?.fueraDeRetencion ?? "—"}
          etiqueta="Registros fuera de retención"
          tono="rojo"
        />
        <Kpi
          valor={r ? `${r.acusesCompletados} %` : "—"}
          etiqueta="Acuses de lectura completados"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-[11.5px] font-semibold text-texto-2">
          Proceso
          <select
            value={proceso}
            onChange={(e) => setProceso(e.target.value)}
            className={`${CLASE_ENTRADA} mt-1 !w-[220px] bg-white`}
          >
            <option value="">Todos</option>
            {procesos.data?.map((p) => (
              <option key={p.codigo} value={p.codigo}>
                {p.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="text-[11.5px] font-semibold text-texto-2">
          Desde
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className={`${CLASE_ENTRADA} mt-1 !w-[160px] bg-white`}
          />
        </label>
        <label className="text-[11.5px] font-semibold text-texto-2">
          Hasta
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className={`${CLASE_ENTRADA} mt-1 !w-[160px] bg-white`}
          />
        </label>
        {(proceso || desde || hasta) && (
          <Boton
            variante="secundario"
            onClick={() => {
              setProceso("");
              setDesde("");
              setHasta("");
            }}
          >
            Quitar filtros
          </Boton>
        )}
      </div>

      <Pestanas opciones={PESTANAS} activa={pestana} onCambio={setPestana} className="mb-4" />

      {pestana === "documentos" && <ControlDocumentos filtro={filtro} generadoEn={r?.generadoEn} />}
      {pestana === "registros" && <ControlRegistros filtro={filtro} />}
      {pestana === "actividad" && <Actividad filtro={filtro} />}
      {pestana === "acuses" && <AcusesDeLectura filtro={filtro} />}
    </div>
  );
}

/** Las celdas de estos reportes tienen varias líneas: todo arriba, alineado. */
const CELDA = `${TD} align-top`;

function CodigoDoc({ codigo }: { codigo: string }) {
  return (
    <Link
      to="/documentos/$codigo"
      params={{ codigo }}
      className="font-mono text-[12px] text-marino hover:underline"
    >
      {codigo}
    </Link>
  );
}

function ControlDocumentos({
  filtro,
  generadoEn,
}: {
  filtro: Filtro;
  generadoEn: Date | undefined;
}) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.auditoria.controlDocumentos.queryOptions(filtro),
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (q.error) return <Aviso>{mensajeDe(q.error)}</Aviso>;
  if (q.data.length === 0)
    return <EstadoVacio>No hay documentos vigentes con ese filtro.</EstadoVacio>;
  return (
    <>
      <p className="mb-2 text-[12px] text-texto-3">
        {q.data.length} documentos
        {generadoEn && ` · generado ${formatearFechaHora(generadoEn)}`}
      </p>
      <Tabla minimo={980}>
        <thead>
          <tr className={CABECERA}>
            <th className={TH}>Código</th>
            <th className={TH}>Documento</th>
            <th className={TH}>Rev.</th>
            <th className={TH}>Aprobado</th>
            <th className={TH}>Elaboró / Revisó / Aprobó</th>
            <th className={TH}>Próx. revisión</th>
            <th className={TH}>Acuses</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((d) => (
            <tr key={d.codigo} className="border-b border-borde/60 last:border-0">
              <td className={CELDA}>
                <CodigoDoc codigo={d.codigo} />
              </td>
              <td className={CELDA}>
                <div className="font-semibold">{d.nombre}</div>
                <div className="text-[11.5px] text-texto-3">{d.proceso}</div>
              </td>
              <td className={`${CELDA} font-mono`}>{formatearRevision(d.revision)}</td>
              <td className={`${CELDA} whitespace-nowrap`}>{formatearFecha(d.aprobadaEn)}</td>
              <td className={CELDA}>
                {[d.elaboro, d.reviso, d.aprobo]
                  .map((n) => (n ? abreviarNombre(n) : "—"))
                  .join(" / ")}
                {!d.tresFirmas && (
                  <div className="text-[11px] text-ambar">Importado: sin firmas en el sistema</div>
                )}
              </td>
              <td
                className={`${CELDA} whitespace-nowrap ${d.revisionVencida ? "font-semibold text-ambar" : ""}`}
              >
                {d.proximaRevision ? formatearDia(d.proximaRevision) : "—"}
                {d.revisionVencida && " ⚠"}
              </td>
              <td
                className={`${CELDA} font-semibold ${
                  d.acuses.total === 0
                    ? "text-texto-3"
                    : d.acuses.leidos === d.acuses.total
                      ? "text-verde"
                      : "text-ambar"
                }`}
              >
                {d.acuses.total === 0 ? "—" : `${d.acuses.leidos}/${d.acuses.total}`}
              </td>
            </tr>
          ))}
        </tbody>
      </Tabla>
    </>
  );
}

function ControlRegistros({ filtro }: { filtro: Filtro }) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.auditoria.controlRegistros.queryOptions(filtro),
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (q.error) return <Aviso>{mensajeDe(q.error)}</Aviso>;
  if (q.data.length === 0)
    return <EstadoVacio>No hay registros vigentes con ese filtro.</EstadoVacio>;
  return (
    <Tabla minimo={1080}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Registro</th>
          <th className={TH}>Ubicación</th>
          <th className={TH}>Indexación</th>
          <th className={TH}>Responsable</th>
          <th className={TH}>Retención</th>
          <th className={TH}>Disposición</th>
        </tr>
      </thead>
      <tbody>
        {q.data.map((d) => (
          <tr key={d.codigo} className="border-b border-borde/60 last:border-0">
            <td className={CELDA}>
              <CodigoDoc codigo={d.codigo} />
            </td>
            <td className={CELDA}>
              <div className="font-semibold">{d.nombre}</div>
              <div className="text-[11.5px] text-texto-3">
                {d.proceso} · Rev. {formatearRevision(d.revision)} · {d.manejo}
              </div>
            </td>
            <td className={CELDA}>
              <div>{d.ubicacionFisica ?? "Sin copia física"}</div>
              <div className="text-[11.5px] text-texto-3">{d.ubicacionDigital}</div>
            </td>
            <td className={`${CELDA} font-mono text-[11.5px]`}>{d.indexacion}</td>
            <td className={CELDA}>{d.responsable}</td>
            <td className={`${CELDA} whitespace-nowrap`}>
              {d.retencionMeses} meses
              <div
                className={`text-[11.5px] ${d.fueraDeRetencion ? "font-semibold text-rojo" : "text-texto-3"}`}
              >
                {d.cumpleEl &&
                  `${d.fueraDeRetencion ? "Cumplió" : "Cumple"} el ${formatearDia(d.cumpleEl)}`}
              </div>
            </td>
            <td className={CELDA}>{d.disposicion}</td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function Actividad({ filtro }: { filtro: Filtro }) {
  const trpc = useTRPC();
  const [buscar, setBuscar] = useState("");
  const [pagina, setPagina] = useState(1);
  const q = useQuery({
    ...trpc.auditoria.actividad.queryOptions({ ...filtro, buscar: buscar || null, pagina }),
    placeholderData: keepPreviousData,
  });
  const cadena = useQuery({ ...trpc.auditoria.verificarCadena.queryOptions(), enabled: false });
  const ultima = useQuery(trpc.auditoria.ultimaVerificacion.queryOptions());
  const paginas = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.tamano)) : 1;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="flex-1 border-l-[3px] border-marino pl-3 text-[12px] text-texto-2">
          Inmutable: cada acción queda con usuario, fecha, hora, canal y dirección IP. Acá no se
          muestra la IP: Soporte Tecnológico la consulta en la ficha del documento.
        </p>
        <input
          type="search"
          value={buscar}
          onChange={(e) => {
            setBuscar(e.target.value);
            setPagina(1);
          }}
          placeholder="Buscar por código o persona…"
          className={`${CLASE_ENTRADA} !w-[240px] bg-white`}
        />
        <Boton variante="secundario" disabled={cadena.isFetching} onClick={() => cadena.refetch()}>
          {cadena.isFetching ? "Verificando…" : "Verificar integridad"}
        </Boton>
      </div>
      {ultima.data !== undefined && (
        <p className="mb-3 text-[12px] text-texto-3">
          {ultima.data === null ? (
            "Todavía no hubo una verificación automática: corre cada lunes a las 7:00."
          ) : ultima.data.intacta ? (
            <>
              Última verificación automática: {formatearFechaHora(ultima.data.ocurridoEn)} ·{" "}
              <span className="font-semibold text-verde">íntegra, {ultima.data.total} eventos</span>
              . Se repite cada lunes a las 7:00.
            </>
          ) : (
            <span className="font-semibold text-rojo">
              Última verificación automática, {formatearFechaHora(ultima.data.ocurridoEn)}: la
              cadena está rota desde el evento {ultima.data.primeraRota}. Avisa a Soporte
              Tecnológico.
            </span>
          )}
        </p>
      )}
      {cadena.data &&
        (cadena.data.intacta ? (
          <div className="mb-3 rounded-md bg-verde-fondo px-3 py-2 text-[12.5px] font-semibold text-verde">
            Cadena íntegra: {cadena.data.total} eventos, ninguno alterado, borrado ni reordenado.
          </div>
        ) : (
          <Aviso className="mb-3">
            La cadena está rota desde el evento {cadena.data.primeraRota}. Avisa a Soporte
            Tecnológico.
          </Aviso>
        ))}
      {q.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {q.error && <Aviso>{mensajeDe(q.error)}</Aviso>}
      {q.data && q.data.eventos.length === 0 && (
        <EstadoVacio>Sin eventos con ese filtro.</EstadoVacio>
      )}
      {q.data && q.data.eventos.length > 0 && (
        <>
          <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
            {q.data.eventos.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 px-4 py-2.5"
              >
                <span className={`flex-1 text-[13px] ${e.esSistema ? "text-texto-2 italic" : ""}`}>
                  {e.descripcion}
                </span>
                <span className="text-[11.5px] whitespace-nowrap text-texto-3">
                  {formatearFechaHora(e.ocurridoEn)}
                  {e.canal === "movil" && <strong className="text-marino"> · móvil</strong>}
                  {e.ip && ` · IP ${e.ip}`}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between text-[12px] text-texto-3">
            <span>
              {q.data.total} eventos · página {pagina} de {paginas}
            </span>
            <span className="flex gap-2">
              <Boton
                variante="secundario"
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => p - 1)}
              >
                Anteriores
              </Boton>
              <Boton
                variante="secundario"
                disabled={pagina >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                Siguientes
              </Boton>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function AcusesDeLectura({ filtro }: { filtro: Filtro }) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.auditoria.acuses.queryOptions(filtro),
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (q.error) return <Aviso>{mensajeDe(q.error)}</Aviso>;
  const conAcuses = q.data.filter((d) => d.total > 0);
  if (conAcuses.length === 0)
    return <EstadoVacio>No hay acuses pedidos con ese filtro.</EstadoVacio>;
  return (
    <Tabla minimo={860}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Documento</th>
          <th className={TH}>Avance</th>
          <th className={TH}>Ya leyeron</th>
          <th className={TH}>Les falta</th>
        </tr>
      </thead>
      <tbody>
        {conAcuses.map((d) => (
          <tr key={d.codigo} className="border-b border-borde/60 last:border-0">
            <td className={CELDA}>
              <CodigoDoc codigo={d.codigo} />
            </td>
            <td className={CELDA}>
              <div className="font-semibold">{d.nombre}</div>
              <div className="text-[11.5px] text-texto-3">
                Rev. {formatearRevision(d.revision)} · vigente desde el{" "}
                {formatearFecha(d.aprobadaEn)}
              </div>
            </td>
            <td
              className={`${CELDA} font-semibold whitespace-nowrap ${d.leidos === d.total ? "text-verde" : "text-ambar"}`}
            >
              {d.leidos} de {d.total}
            </td>
            <td className={CELDA}>
              {d.personas
                .filter((p) => p.leidoEn)
                .map((p) => (
                  <div key={p.nombre}>
                    {abreviarNombre(p.nombre)}{" "}
                    <span className="text-[11.5px] text-texto-3">
                      {p.leidoEn && formatearFecha(p.leidoEn)}
                      {p.canal === "movil" && " · móvil"}
                    </span>
                  </div>
                ))}
            </td>
            <td className={`${CELDA} text-ambar`}>
              {d.personas
                .filter((p) => !p.leidoEn)
                .map((p) => abreviarNombre(p.nombre))
                .join(", ") || <span className="text-verde">Nadie</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}
